import type { ServerMetadata } from "@app/lib/actions/mcp_internal_actions/tool_definition";
import { z } from "zod";

export const FRONT_TOOLS_METADATA = [
  {
    name: "search_conversations",
    description:
      "Search or find conversations in the Front inbox by keywords, customer email, tags, status, or other criteria. Returns matching conversations with their details.",
    schema: {
      q: z
        .string()
        .describe(
          "Search query: keywords, customer email, status (is:open, is:archived), or tags (tag:bug)."
        ),
      limit: z
        .number()
        .optional()
        .default(20)
        .describe(
          "Maximum number of conversations to return (default: 20, max: 20)"
        ),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Searching Front conversations",
      done: "Search Front conversations",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "get_conversation",
    description:
      "Retrieve complete details of a specific conversation by its ID, including subject, status, " +
      "assignee, tags, inbox, and all metadata.",
    schema: {
      conversation_id: z
        .string()
        .describe("The unique ID of the conversation (e.g., 'cnv_55c8c149')"),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Retrieving Front conversation",
      done: "Retrieve Front conversation",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "get_conversation_messages",
    description:
      "Retrieve all messages in a conversation, including both external messages (emails) and " +
      "internal comments. Returns the complete message timeline.",
    schema: {
      conversation_id: z
        .string()
        .describe("The unique ID of the conversation (e.g., 'cnv_55c8c149')"),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Retrieving Front messages",
      done: "Retrieve Front messages",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "get_contact",
    description:
      "Look up customer/contact information by email address or contact ID. Returns contact details " +
      "including name, email, and custom fields.",
    schema: {
      email: z
        .string()
        .optional()
        .describe("Email address of the contact to look up"),
      contact_id: z
        .string()
        .optional()
        .describe("The unique ID of the contact (e.g., 'crd_55c8c149')"),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Looking up Front contact",
      done: "Look up Front contact",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "list_tags",
    description: "Get all available tags for categorizing conversations.",
    schema: {},
    stake: "never_ask",
    displayLabels: {
      running: "Listing Front tags",
      done: "List Front tags",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "list_teammates",
    description:
      "Get all teammates in the workspace for assignment and collaboration.",
    schema: {},
    stake: "never_ask",
    displayLabels: {
      running: "Listing Front teammates",
      done: "List Front teammates",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "get_customer_history",
    description:
      "Retrieve a customer's past conversations in Front, by their email address.",
    schema: {
      customer_email: z
        .string()
        .describe("Customer email address to search for"),
      limit: z
        .number()
        .optional()
        .default(10)
        .describe(
          "Maximum number of past conversations to return (default: 10, max: 20)"
        ),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Retrieving Front customer history",
      done: "Retrieve Front customer history",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "list_inboxes",
    description: "Get all inboxes/channels available in the workspace.",
    schema: {},
    stake: "never_ask",
    displayLabels: {
      running: "Listing Front inboxes",
      done: "List Front inboxes",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "get_conversation_drafts",
    description:
      "List all drafts in a conversation. Returns draft IDs and version tokens needed for editing or deleting drafts.",
    schema: {
      conversation_id: z
        .string()
        .describe("The unique ID of the conversation (e.g., 'cnv_55c8c149')"),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Retrieving Front conversation drafts",
      done: "Retrieve Front conversation drafts",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "create_conversation",
    description: "Start a new outbound conversation with a customer.",
    schema: {
      inbox_id: z
        .string()
        .describe("The ID of the inbox to create the conversation in"),
      to: z.array(z.string()).describe("Array of recipient email addresses"),
      subject: z.string().describe("Subject line of the conversation"),
      body: z.string().describe("Message body content"),
      author_id: z
        .string()
        .optional()
        .describe("Optional: Teammate ID for the message author"),
    },
    stake: "low",
    displayLabels: {
      running: "Creating Front conversation",
      done: "Create Front conversation",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "create_draft",
    description:
      "Create a draft reply to a conversation for review before sending.",
    schema: {
      conversation_id: z.string().describe("The unique ID of the conversation"),
      body: z.string().describe("The draft message content"),
      author_id: z
        .string()
        .optional()
        .describe("Optional: Teammate ID for the draft author"),
    },
    stake: "low",
    displayLabels: {
      running: "Creating draft on Front",
      done: "Create draft on Front",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "delete_draft",
    description:
      "Delete a draft that is no longer needed. Requires the version token from get_conversation_drafts for optimistic concurrency.",
    schema: {
      draft_id: z
        .string()
        .describe(
          "The unique ID of the draft to delete (e.g., 'msg_draft_xyz')"
        ),
      version: z
        .string()
        .describe(
          "Optimistic concurrency token from get_conversation_drafts. Required to prevent deleting a draft that has been modified."
        ),
    },
    stake: "high",
    displayLabels: {
      running: "Deleting draft on Front",
      done: "Delete draft on Front",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "add_tags",
    description:
      "Add one or more tags to a conversation for categorization (e.g., bug, feature-request, billing).",
    schema: {
      conversation_id: z.string().describe("The unique ID of the conversation"),
      tag_ids: z
        .array(z.string())
        .describe("Array of tag IDs to add to the conversation"),
    },
    stake: "low",
    displayLabels: {
      running: "Adding tags on Front",
      done: "Add tags on Front",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "add_comment",
    description:
      "Add an internal comment or note to a Front conversation, visible only to your team.",
    schema: {
      conversation_id: z.string().describe("The unique ID of the conversation"),
      body: z.string().describe("The comment content"),
      author_id: z
        .string()
        .optional()
        .describe("Optional: Teammate ID for the comment author"),
    },
    stake: "low",
    displayLabels: {
      running: "Adding comment on Front",
      done: "Add comment on Front",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "add_links",
    description:
      "Link related conversations together for better tracking and context.",
    schema: {
      conversation_id: z.string().describe("The unique ID of the conversation"),
      linked_conversation_ids: z
        .array(z.string())
        .describe("Array of conversation IDs to link to"),
    },
    stake: "low",
    displayLabels: {
      running: "Linking Front conversations",
      done: "Link Front conversations",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "send_message",
    description:
      "Reply or respond to a customer on a Front conversation. Sends a customer-facing email by default. Can also post an internal note.",
    schema: {
      conversation_id: z.string().describe("The unique ID of the conversation"),
      body: z.string().describe("The message content (supports markdown)"),
      type: z
        .enum(["comment", "reply"])
        .default("reply")
        .describe(
          "Type of message: 'comment' for internal note, 'reply' for customer-facing email (default: reply)"
        ),
      author_id: z
        .string()
        .optional()
        .describe(
          "Optional: Teammate ID to send as (defaults to API token owner)"
        ),
    },
    stake: "high",
    displayLabels: {
      running: "Sending message on Front",
      done: "Send message on Front",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "update_conversation_status",
    description:
      "Update the status of a Front conversation: archive (close), reopen, delete, mark as spam, or move to trash.",
    schema: {
      conversation_id: z.string().describe("The unique ID of the conversation"),
      status: z
        .enum(["archived", "deleted", "open", "spam", "trash"])
        .describe(
          "New status: 'archived' (close), 'open' (reopen), 'deleted', 'spam', 'trash'"
        ),
    },
    stake: "high",
    displayLabels: {
      running: "Updating Front conversation status",
      done: "Update Front conversation status",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "assign_conversation",
    description: "Assign a conversation to a specific teammate for handling.",
    schema: {
      conversation_id: z.string().describe("The unique ID of the conversation"),
      teammate_id: z.string().describe("The ID of the teammate to assign to"),
    },
    stake: "high",
    displayLabels: {
      running: "Assigning Front conversation",
      done: "Assign Front conversation",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
] as const;

export const FRONT_SERVER = {
  serverInfo: {
    name: "front",
    version: "1.0.0",
    description:
      "Manage support conversations, messages, and customer interactions.",
    authorization: null,
    icon: "FrontLogo",
    documentationUrl: "https://docs.laufwerk.artech-is.com/tools/tools-anbinden#ein-tool-einrichten",
  },
  tools: FRONT_TOOLS_METADATA,
} as const satisfies ServerMetadata;
